import { User } from "../gtm/User";
import "../global";
import { UserIdExchange } from "./UserIdExchange";
import { getUserAdapter } from "../gtm/GtmUtils";
import { inject, injectable, singleton } from "tsyringe";
import { UserDataLayerAdapter } from "./UserDataLayerAdapter";
import { UserDataLayerAdapterFactory } from "./UserDataLayerAdapterFactory";
import { ServiceTokens } from "./ServiceTokens";
import { Logger } from "../utils/Logger";

@singleton()
@injectable()
export class UserManager {
  constructor(
    @inject(UserIdExchange) private userIdExchange: UserIdExchange,
    @inject(ServiceTokens.UserDataLayerAdapterFactory) private userDataLayerAdapterFactory: UserDataLayerAdapterFactory,
  ) {}

  /**
   * Initialize the user and update the dataLayer && Update and send data to the UIE.
   */
  public async initDataLayerUserAndUIE(): Promise<void> {
    await this.initDataLayerUser();
    await this.initUserFromUIE();
  }

  /**
   * Initialize the user and update the dataLayer
   */
  public async initDataLayerUser(): Promise<User> {
    let userAdapter = getUserAdapter();
    if (!userAdapter) {
      userAdapter = this.userDataLayerAdapterFactory.getUserDataAdapter({});
      this.pushUserToDataLayer(userAdapter, "initDataLayerUser");
    }
    return userAdapter;
  }

  /**
   * Update and send data to the UIE.
   */
  public async initUserFromUIE(): Promise<void> {
    const userAdapter = getUserAdapter();
    if (userAdapter) {
      await this.userIdExchange.initUserFromUIE(userAdapter);
    }
    this.validateIdentifiers();
  }

  /**
   * Update the user in the datalayer
   * @param userDataLayerAdapter User adapter
   * @param eventName The name of the event
   */
  private pushUserToDataLayer(userDataLayerAdapter: UserDataLayerAdapter, eventName: string): void {
    dataLayerQueue.push({
      event: `gtmLib.${eventName}`,
      user: userDataLayerAdapter.dataLayerUser,
    });
  }

  private validateIdentifiers(): void {
    const userAdapter = getUserAdapter();
    if (!userAdapter) {
      Logger.error("No user found after initialization!");
      return;
    }
    if (userAdapter.rcId && userAdapter.rc_visitor_id) {
      Logger.error(
        `The dataLayer user has both an RCID (${userAdapter.rcId}) and a VisitorID (${userAdapter.rc_visitor_id})!`,
      );
    }
    if (userAdapter.rcId && !userAdapter.rc_plus_id) {
      const errorMsg = `The dataLayer user has a RCID (${userAdapter.rcId}) but no RCPlus!`;
      Logger.error(errorMsg);
    }
    if (!userAdapter.rcId && !userAdapter.rc_visitor_id) {
      Logger.error(`The dataLayer user has neither RCID nor VisitorID!`);
    }
  }
}
